import PropTypes from 'prop-types';
import { Icon, Modal, ModalHeader, ModalBody } from '@evertrue/et-components';

const ACTION_STEP_TYPES = [
  {
    id: 'email',
    label: 'Automated Email',
    description: 'Send an automated email to recipients',
    icon: 'email',
  },
  {
    id: 'task',
    label: 'Task',
    description: 'Create a task - Non-automated email, call, research, and more',
    icon: 'task',
  },
];

const FLOW_STEP_TYPES = [
  {
    id: 'wait',
    label: 'Add Wait Time',
    description: 'Pause the cadence for a set duration',
    icon: 'access-time',
  },
  {
    id: 'condition',
    label: 'Add Condition',
    description: 'Branch based on recipient behavior',
    icon: 'filter-list',
  },
  {
    id: 'exit',
    label: 'Exit Cadence',
    description: 'Remove contact from cadence early',
    icon: 'exit-to-app',
  },
];

// Types that open a follow-up modal — parent manages closing the add-step modal
const DEFERRED_TYPES = ['condition', 'task'];

/**
 * Modal for adding a new step to the cadence flow.
 * Shows action steps (email, manual task) and flow control steps (wait, condition)
 * in separate labeled sections.
 */
const AddStepModal = ({ isOpen, onClose, onSelectStepType, availableTypes = null, isBranchContext = false }) => {
  const handleSelect = (stepType) => {
    onSelectStepType(stepType);
    if (!DEFERRED_TYPES.includes(stepType.id)) {
      onClose();
    }
  };

  const filterTypes = (types) => {
    if (!availableTypes) return types.filter((st) => st.id !== 'exit');
    return types.filter((st) => availableTypes.includes(st.id));
  };

  const actionTypes = filterTypes(ACTION_STEP_TYPES);
  const flowTypes = filterTypes(FLOW_STEP_TYPES);
  const modalTitle = isBranchContext ? 'Add Branch Step' : 'Add Step';

  const renderOption = (stepType) => (
    <button key={stepType.id} type="button" className="add-step-modal--option" onClick={() => handleSelect(stepType)}>
      <div className="add-step-modal--option-icon">
        <Icon icon={stepType.icon} />
      </div>
      <div className="add-step-modal--option-content">
        <span className="add-step-modal--option-label">{stepType.label}</span>
        <span className="add-step-modal--option-desc">{stepType.description}</span>
      </div>
    </button>
  );

  return (
    <Modal isOpen={isOpen} closeModal={onClose} size="medium">
      <ModalHeader title={modalTitle} closeModal={onClose} />
      <ModalBody>
        <div className="add-step-modal">
          {actionTypes.length > 0 && (
            <div className="add-step-modal--section">
              <span className="add-step-modal--section-label">Action Steps</span>
              <div className="add-step-modal--options">{actionTypes.map(renderOption)}</div>
            </div>
          )}
          {actionTypes.length > 0 && flowTypes.length > 0 && <div className="add-step-modal--section-divider" />}
          {flowTypes.length > 0 && (
            <div className="add-step-modal--section">
              <span className="add-step-modal--section-label">Flow Controls</span>
              <div className="add-step-modal--options">{flowTypes.map(renderOption)}</div>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

AddStepModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectStepType: PropTypes.func.isRequired,
  availableTypes: PropTypes.arrayOf(PropTypes.string),
  isBranchContext: PropTypes.bool,
};

export default AddStepModal;
